/**
 * LinkedEntityPicker — sélecteur universel de « fiche liée » (WKF-013 §5.4).
 *
 * Réutilise la recherche globale ⌘K : on tape, on choisit une fiche de n'importe
 * quel type autorisé (client, apprenant, formation, lieu, restauration,
 * hébergement, frais, article, contact, document, analyse de besoins…). À la
 * sélection, on calcule {type, id, libellé, URL} via le registre de recherche —
 * le lien devient cliquable. Pré-remplissable depuis le contexte, modifiable et
 * supprimable.
 */

import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { ExternalLink, Link2, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from '@/components/ui/command'
import { Label } from '@/components/ui/label'
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { useDebounce } from '@/hooks/useDebounce'
import { useLinkedEntitySearch } from '@/hooks/useLinkedEntitySearch'
import { SearchHitRow } from '@/components/search/SearchHitRow'
import { CATEGORY_REGISTRY, type SearchHit } from '@/lib/searchCategories'
import { groupHitsByRelevance } from '@/lib/searchGrouping'
import {
    linkedEntityFromHit,
    linkedEntityIcon,
    type LinkedEntityValue,
} from '@/lib/linkedEntity'

/**
 * Plafond serveur PAR TYPE — `global_search.py` : `WHERE rn <= 7`.
 * Recopié ici pour SIGNALER la troncature, jamais pour la produire : si le
 * serveur change de plafond, la mention devient au pire imprécise, elle ne
 * fabrique aucun défaut.
 */
const PER_TYPE_CAP = 7

interface Props {
    value: LinkedEntityValue | null
    onChange: (value: LinkedEntityValue | null) => void
    entityId: string | null
    disabled?: boolean
    /**
     * Le refus que le serveur opposerait au lien choisi, dit SOUS le champ — la modale de détail
     * y met celui de relier à un AO la tâche d'un collègue qui ne le voit pas (PO n° 66).
     */
    refus?: string | null
}

export function LinkedEntityPicker({ value, onChange, entityId, disabled, refus }: Props) {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const debounced = useDebounce(query, 250)
    const { hits, isLoading, isError, isTimeout, typosSkipped, enabled } = useLinkedEntitySearch(debounced, entityId)

    // Regroupement par type, MÊME ordre que la palette ⌘K : la pertinence
    // renvoyée par le serveur d'abord, l'ordre du registre en départage. Trier
    // par le seul ordre du registre enterrait le meilleur résultat sous les
    // groupes de bruit flou des types « prioritaires » (cf. searchGrouping).
    const groups = useMemo(() => groupHitsByRelevance(hits), [hits])

    function select(hit: SearchHit) {
        onChange(linkedEntityFromHit(hit))
        setOpen(false)
        setQuery('')
    }

    const searchPanel = (
        <Command shouldFilter={false} className="w-full">
            <CommandInput
                value={query}
                onValueChange={setQuery}
                placeholder={t('notifications.task.linkSearch', 'Rechercher une fiche…')}
                autoFocus
            />
            {/* Le registre compte 23 catégories : la hauteur par défaut de
                `CommandList` (300 px, ~8 lignes) coupait les derniers groupes.
                `min(…, 60vh)` est indispensable — ce champ est le DERNIER de la
                modale, Radix bascule alors le popover vers le haut.

                ⚠️ La MOLETTE dans cette liste ne fonctionne que grâce au garde
                posé dans `ui/popover.tsx` : ouvert dans une modale, ce panneau
                part dans un portail sur `document.body`, hors du verrou de
                `react-remove-scroll` — qui annulait alors tout `wheel`. Mesuré :
                352 px de fenêtre pour 728 px de contenu, `scrollTop` immobile. */}
            <CommandList className="max-h-[min(60vh,22rem)]">
                {/* Une recherche refusée n'est pas une recherche vide : au-delà de son
                    délai, le serveur rend `search_timeout` et la fiche existe peut-être. */}
                {enabled && !isLoading && hits.length === 0 && (
                    <CommandEmpty>
                        {isTimeout
                            ? t('globalSearch.state.timeout')
                            : isError
                              ? t('globalSearch.state.error')
                              : typosSkipped
                                ? t('globalSearch.state.typos_skipped')
                                : t('notifications.task.linkNoResult', 'Aucune fiche trouvée.')}
                    </CommandEmpty>
                )}
                {!enabled && (
                    <div className="py-4 text-center text-xs text-muted-foreground">
                        {t('notifications.task.linkHint', 'Tapez au moins 3 caractères.')}
                    </div>
                )}
                {/* Squelette pendant la frappe : sans lui, le panneau reste vide et
                    l'utilisateur croit qu'il n'y a rien (iso palette ⌘K). */}
                {enabled && isLoading && hits.length === 0 && (
                    <div className="space-y-2 p-2" aria-hidden="true">
                        {[0, 1, 2].map((i) => (
                            <div
                                key={i}
                                className="h-9 rounded bg-muted animate-pulse motion-reduce:animate-none"
                            />
                        ))}
                    </div>
                )}
                {groups.map(({ type, hits: typeHits }) => {
                    const Icon = CATEGORY_REGISTRY[type].icon
                    return (
                        <CommandGroup
                            key={type}
                            // Icône SUR L'EN-TÊTE, jamais répétée ligne à ligne :
                            // convention de la palette (§2.2 — le domaine se lit à
                            // la forme de l'icône et au titre du groupe).
                            heading={
                                <span className="flex items-center gap-1.5">
                                    <Icon className="h-3 w-3" aria-hidden="true" />
                                    {t(`globalSearch.group.${CATEGORY_REGISTRY[type].i18nKey}`)}
                                </span>
                            }
                        >
                            {typeHits.map((h) => (
                                <CommandItem
                                    key={`${h.type}:${h.id}`}
                                    value={`${h.type}:${h.id}`}
                                    onSelect={() => select(h)}
                                    className="gap-2.5"
                                >
                                    <SearchHitRow hit={h} query={debounced} />
                                </CommandItem>
                            ))}
                        </CommandGroup>
                    )
                })}
                {/* Le serveur plafonne à 7 lignes PAR TYPE (`global_search.py`,
                    `WHERE rn <= 7`). Un groupe plein est donc peut-être tronqué, et
                    ce sélecteur n'a pas de « Voir tout » (la plupart de ces listes
                    ne lisent pas `?q=`). Le taire ferait lire « il n'y en a que
                    sept » là où il y en a peut-être quarante. */}
                {/* Correction des fautes abandonnée faute de temps : il manque peut-être des fiches. */}
                {enabled && typosSkipped && hits.length > 0 && (
                    <div className="border-t px-3 py-2 text-[11px] text-muted-foreground">
                        {t('globalSearch.state.typos_skipped')}
                    </div>
                )}
                {groups.some((g) => g.hits.length >= PER_TYPE_CAP) && (
                    <div className="border-t px-3 py-2 text-[11px] text-muted-foreground">
                        {t(
                            'notifications.task.linkTruncated',
                            'Seules les {{count}} premières fiches par catégorie sont affichées — précisez votre recherche.',
                            { count: PER_TYPE_CAP },
                        )}
                    </div>
                )}
            </CommandList>
        </Command>
    )

    const ValueIcon = linkedEntityIcon(value?.type)

    return (
        <div className="space-y-1.5">
            <Label className="text-sm font-medium">
                {t('notifications.task.linkLabel', 'Fiche liée')}
            </Label>
            {/* UN SEUL Popover pour les deux états, ANCRÉ sur le champ entier et
                non sur le bouton qui l'ouvre.

                ⚠️ Sans cet ancrage, le panneau débordait de la modale : « Changer »
                est un petit bouton fantôme, et un panneau dimensionné en absolu
                (34 rem = 544 px) dépasse la colonne de contenu d'une `max-w-lg`
                (512 px de modale, 464 px une fois les 24 px de marge de chaque
                côté retirés). Un panneau plus large que la couche qui le porte ne
                se lit plus comme SON menu — il flotte par-dessus la modale.

                `PopoverAnchor` fait pointer `--radix-popover-trigger-width` sur ce
                conteneur (cf. `react-popover` : avec une ancre explicite, la
                variable vaut `--radix-popper-anchor-width`, soit l'ancre et non le
                déclencheur). Le panneau fait donc EXACTEMENT la largeur du champ,
                dans les deux états, quelle que soit la taille de la modale. */}
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverAnchor asChild>
                    <div className="w-full">
                        {value ? (
                            <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2 text-sm">
                                {ValueIcon && (
                                    <ValueIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                                )}
                                {/* Libellé CLIQUABLE → ouvre la fiche liée (navigation in-app), y
                                    compris depuis la modale ouverte (WKF-013 §5.4). */}
                                {value.href ? (
                                    <button
                                        type="button"
                                        onClick={() => navigate(value.href)}
                                        className="flex min-w-0 flex-1 items-center gap-1 truncate text-left hover:text-foreground hover:underline focus-visible:underline"
                                        title={t('notifications.detail.openSource', 'Ouvrir la fiche liée')}
                                    >
                                        <span className="truncate">{value.label}</span>
                                        <ExternalLink className="h-3.5 w-3.5 shrink-0 opacity-60" aria-hidden />
                                    </button>
                                ) : (
                                    <span className="flex-1 truncate">{value.label}</span>
                                )}
                                {!disabled && (
                                    <>
                                        <PopoverTrigger asChild>
                                            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs">
                                                {t('notifications.task.linkChange', 'Changer')}
                                            </Button>
                                        </PopoverTrigger>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-7 w-7"
                                            onClick={() => onChange(null)}
                                            aria-label={t('notifications.task.unlink', 'Retirer le lien')}
                                        >
                                            <X className="h-3.5 w-3.5" />
                                        </Button>
                                    </>
                                )}
                            </div>
                        ) : (
                            <PopoverTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="w-full justify-start font-normal text-muted-foreground"
                                    disabled={disabled}
                                >
                                    <Link2 className="mr-2 h-4 w-4" aria-hidden />
                                    {t('notifications.task.linkAdd', 'Relier une fiche…')}
                                </Button>
                            </PopoverTrigger>
                        )}
                    </div>
                </PopoverAnchor>
                <PopoverContent
                    // Exactement la largeur du champ ancré — jamais plus, pour que le
                    // panneau reste DANS la modale (cf. le commentaire de l'ancre).
                    // Les 320 px d'avant tronquaient tout ce qui compte
                    // (« Sylvie LA… », « Bilan de c… ») ; ici la largeur est celle du
                    // formulaire, donc elle suit la modale au lieu de la contredire.
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                >
                    {searchPanel}
                </PopoverContent>
            </Popover>
            {refus && (
                <p role="alert" className="text-xs text-destructive">
                    {refus}
                </p>
            )}
        </div>
    )
}
